import type { MouseEvent } from 'react'
import { useEffect, useMemo, useState } from 'react'
import { ChevronDownIcon, XIcon } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  Combobox,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxInput,
  ComboboxItem,
  ComboboxList,
  ComboboxTrigger,
} from '@/components/ui/combobox'
import { cn } from '@/shared/lib/utils'
import { useListPackagingSuppliers } from '@/shared/hooks/api/packagingSuppliers'
import {
  ensureSelectedSupplierOption,
  type PackagingItemSupplierFilterOption,
} from './packagingItemSupplierFilterOptions'

type PackagingItemsSupplierFilterComboboxProps = {
  id?: string
  className?: string
  value: string | null
  selectedLabel?: string
  onValueChange: (supplierId: string | null) => void
  placeholder: string
  searchPlaceholder: string
  emptyText: string
  clearLabel: string
}

export function PackagingItemsSupplierFilterCombobox({
  id,
  className,
  value,
  selectedLabel,
  onValueChange,
  placeholder,
  searchPlaceholder,
  emptyText,
  clearLabel,
}: PackagingItemsSupplierFilterComboboxProps) {
  const [open, setOpen] = useState(false)
  const [inputValue, setInputValue] = useState('')
  const [debouncedQuery, setDebouncedQuery] = useState('')

  useEffect(() => {
    const handle = window.setTimeout(() => {
      setDebouncedQuery(inputValue.trim())
    }, 200)
    return () => {
      window.clearTimeout(handle)
    }
  }, [inputValue])

  const { data: suppliersList, isFetching } = useListPackagingSuppliers(
    {
      page: 1,
      pageSize: 50,
      searchQuery: debouncedQuery.length > 0 ? debouncedQuery : undefined,
    },
    open
  )

  const options = useMemo(() => {
    const fromApi: PackagingItemSupplierFilterOption[] = (
      suppliersList?.suppliers ?? []
    ).map((supplier) => ({
      id: supplier.id,
      name: supplier.name,
    }))
    return ensureSelectedSupplierOption(fromApi, value, selectedLabel)
  }, [selectedLabel, suppliersList?.suppliers, value])

  const selectedOption = useMemo(
    () => options.find((item) => item.id === value) ?? null,
    [options, value]
  )

  const triggerLabel =
    selectedOption != null ? selectedOption.name : placeholder

  const handleClear = (event: MouseEvent) => {
    event.preventDefault()
    event.stopPropagation()
    onValueChange(null)
  }

  return (
    <Combobox
      id={id}
      items={options.map((item) => item.id)}
      filteredItems={options.map((item) => item.id)}
      filter={null}
      autoHighlight={false}
      open={open}
      onOpenChange={(nextOpen) => {
        setOpen(nextOpen)
        if (!nextOpen) {
          setInputValue('')
          setDebouncedQuery('')
        }
      }}
      inputValue={inputValue}
      onInputValueChange={(next) => {
        setInputValue(next ?? '')
      }}
      value={value}
      onValueChange={(supplierId) => {
        onValueChange(supplierId)
        setOpen(false)
        setInputValue('')
        setDebouncedQuery('')
      }}
      isItemEqualToValue={(left, right) => left === right}
    >
      <ComboboxTrigger
        render={
          <Button
            type='button'
            variant='outline'
            size='sm'
            className={cn(
              'h-8 w-full justify-between gap-2 border-dashed px-3 text-sm font-normal shadow-xs',
              className
            )}
          >
            <span
              className={cn(
                'truncate',
                selectedOption == null && 'text-muted-foreground'
              )}
            >
              {triggerLabel}
            </span>
            <span className='flex shrink-0 items-center gap-1'>
              {value != null && value !== '' ? (
                <span
                  role='button'
                  tabIndex={0}
                  className='rounded-sm p-0.5 hover:bg-muted'
                  aria-label={clearLabel}
                  onClick={handleClear}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      handleClear(event as unknown as MouseEvent)
                    }
                  }}
                >
                  <XIcon className='size-3.5 opacity-60' />
                </span>
              ) : null}
              <ChevronDownIcon className='size-4 opacity-50' />
            </span>
          </Button>
        }
      />
      <ComboboxContent className='w-[var(--anchor-width)] min-w-[220px] p-0'>
        <ComboboxInput showTrigger={false} placeholder={searchPlaceholder} />
        <ComboboxList>
          <ComboboxEmpty>
            {isFetching ? searchPlaceholder : emptyText}
          </ComboboxEmpty>
          {options.map((option) => (
            <ComboboxItem key={option.id} value={option.id}>
              {option.name}
            </ComboboxItem>
          ))}
        </ComboboxList>
      </ComboboxContent>
    </Combobox>
  )
}
